import { useEffect, useRef, useState } from "react"
import InputMask from "react-input-mask"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"
import { Check, ChevronDown } from "lucide-react"

export interface SubscriptionsByEventFilterProps {
  search: string
  onSearchChange: (value: string) => void
  inscriptionFilters: string[]
  onInscriptionFiltersChange: (values: string[]) => void
  typeFilter: string
  onTypeFilterChange: (value: string) => void
  periodFilter: string
  onPeriodFilterChange: (value: string) => void
  startDate: Date | null
  onStartDateChange: (date: Date | null) => void
  endDate: Date | null
  onEndDateChange: (date: Date | null) => void
  defaultStartText?: string
  defaultEndText?: string
}

const STATUS_OPTIONS = [
  { value: "pendentes", label: "Pendentes" },
  { value: "aprovados", label: "Aprovados" },
  { value: "recusados", label: "Recusados" },
  { value: "encerrados", label: "Encerrados" },
]

const TYPE_LABELS: Record<string, string> = {
  Onlive: "Onlives",
  Presencial: "Presenciais",
}

const PERIOD_LABELS: Record<string, string> = {
  "este-mes": "Este mês",
  "proximo-mes": "Próximo mês",
  "este-ano": "Este ano",
}

function parseBRDate(value: string): Date | null {
  const digits = value.replace(/\D/g, "")
  if (digits.length !== 8) return null
  const day = parseInt(digits.slice(0, 2))
  const month = parseInt(digits.slice(2, 4)) - 1
  const year = parseInt(digits.slice(4, 8))
  const date = new Date(year, month, day)
  return isNaN(date.getTime()) ? null : date
}

export const SubscriptionsByEventFilter: React.FC<SubscriptionsByEventFilterProps> = ({
  search,
  onSearchChange,
  inscriptionFilters,
  onInscriptionFiltersChange,
  typeFilter,
  onTypeFilterChange,
  periodFilter,
  onPeriodFilterChange,
  onStartDateChange,
  onEndDateChange,
  defaultStartText = "",
  defaultEndText = "",
}) => {
  const [startText, setStartText] = useState(defaultStartText)
  const [endText, setEndText] = useState(defaultEndText)
  const [committedStart, setCommittedStart] = useState(defaultStartText)
  const [committedEnd, setCommittedEnd] = useState(defaultEndText)

  const [statusOpen, setStatusOpen] = useState(false)
  const statusRef = useRef<HTMLDivElement>(null)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftInscription, setDraftInscription] = useState<string[]>([])
  const [draftType, setDraftType] = useState("todos")
  const [draftPeriod, setDraftPeriod] = useState("todos")
  const [draftStartText, setDraftStartText] = useState("")
  const [draftEndText, setDraftEndText] = useState("")

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
        setStatusOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const toggleStatus = (value: string) => {
    const next = inscriptionFilters.includes(value)
      ? inscriptionFilters.filter((v) => v !== value)
      : [...inscriptionFilters, value]
    onInscriptionFiltersChange(next)
  }

  const statusLabel =
    inscriptionFilters.length === 0
      ? "Todos os Status"
      : inscriptionFilters.length === 1
        ? (STATUS_OPTIONS.find((o) => o.value === inscriptionFilters[0])?.label ?? inscriptionFilters[0])
        : `${inscriptionFilters.length} selecionados`

  const hasActiveFilters =
    inscriptionFilters.length > 0 || typeFilter !== "todos" || periodFilter !== "todos"

  const openDrawer = () => {
    setDraftInscription(inscriptionFilters)
    setDraftType(typeFilter)
    setDraftPeriod(periodFilter)
    setDraftStartText(committedStart)
    setDraftEndText(committedEnd)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onInscriptionFiltersChange(draftInscription)
    onTypeFilterChange(draftType)
    onPeriodFilterChange(draftPeriod)
    if (draftPeriod === "customizado") {
      const s = parseBRDate(draftStartText)
      const e = parseBRDate(draftEndText)
      onStartDateChange(s)
      onEndDateChange(e)
      setCommittedStart(s ? draftStartText : "")
      setCommittedEnd(e ? draftEndText : "")
      setStartText(draftStartText)
      setEndText(draftEndText)
    } else {
      onStartDateChange(null)
      onEndDateChange(null)
      setCommittedStart("")
      setCommittedEnd("")
    }
    setDrawerOpen(false)
  }

  const handleRemovePeriod = () => {
    onPeriodFilterChange("todos")
    onStartDateChange(null)
    onEndDateChange(null)
    setStartText("")
    setEndText("")
    setCommittedStart("")
    setCommittedEnd("")
  }

  const clearFilters = () => {
    onInscriptionFiltersChange([])
    onTypeFilterChange("todos")
    handleRemovePeriod()
    setDraftInscription([])
    setDraftType("todos")
    setDraftPeriod("todos")
    setDraftStartText("")
    setDraftEndText("")
    setDrawerOpen(false)
  }

  const handleStartBlur = () => {
    const date = parseBRDate(startText)
    if (date) { setCommittedStart(startText); onStartDateChange(date) }
    else { setCommittedStart(""); onStartDateChange(null) }
  }

  const handleEndBlur = () => {
    const date = parseBRDate(endText)
    if (date) { setCommittedEnd(endText); onEndDateChange(date) }
    else { setCommittedEnd(""); onEndDateChange(null) }
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  for (const value of inscriptionFilters) {
    const option = STATUS_OPTIONS.find((o) => o.value === value)
    if (option) {
      activeChips.push({
        key: `status-${value}`,
        label: option.label,
        onRemove: () => onInscriptionFiltersChange(inscriptionFilters.filter((v) => v !== value)),
      })
    }
  }

  if (typeFilter !== "todos") {
    activeChips.push({
      key: "type",
      label: TYPE_LABELS[typeFilter] ?? typeFilter,
      onRemove: () => onTypeFilterChange("todos"),
    })
  }

  if (periodFilter !== "todos") {
    if (periodFilter === "customizado") {
      if (committedStart || committedEnd) {
        const parts = []
        if (committedStart) parts.push(`De: ${committedStart}`)
        if (committedEnd) parts.push(`Até: ${committedEnd}`)
        activeChips.push({ key: "period", label: parts.join(" "), onRemove: handleRemovePeriod })
      }
    } else {
      activeChips.push({
        key: "period",
        label: PERIOD_LABELS[periodFilter] ?? periodFilter,
        onRemove: handleRemovePeriod,
      })
    }
  }

  return (
    <div>
      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar evento..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 size-3 rounded-full bg-orange-500" />
            )}
          </button>
        </div>
        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Buscar evento..."
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          {/* Status multiselect */}
          <div ref={statusRef} className="relative">
            <button
              onClick={() => setStatusOpen((v) => !v)}
              className={`flex h-9 w-48 items-center justify-between rounded-md border px-3 text-sm transition-colors ${
                inscriptionFilters.length > 0
                  ? "border-orange-500 bg-orange-500/10 text-orange-500"
                  : "border-border bg-surface text-foreground"
              }`}
            >
              <span className="truncate">{statusLabel}</span>
              <ChevronDown className={`ml-2 size-4 shrink-0 transition-transform ${statusOpen ? "rotate-180" : ""}`} />
            </button>

            {statusOpen && (
              <div className="absolute left-0 top-full z-50 mt-1 w-48 rounded-md border border-border bg-surface shadow-lg">
                {STATUS_OPTIONS.map((option) => {
                  const selected = inscriptionFilters.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      onClick={() => toggleStatus(option.value)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-white/5"
                    >
                      <div
                        className={`flex size-4 shrink-0 items-center justify-center rounded border transition-colors ${
                          selected ? "border-orange-500 bg-orange-500" : "border-border"
                        }`}
                      >
                        {selected && <Check className="size-3 text-white" />}
                      </div>
                      <span className={selected ? "text-orange-500" : "text-foreground"}>
                        {option.label}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <Select onValueChange={onTypeFilterChange} value={typeFilter}>
            <SelectTrigger
              className={`w-40 border-border bg-surface ${typeFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todos os Tipos" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="todos">Todos os Tipos</SelectItem>
              <SelectItem value="Onlive">Onlives</SelectItem>
              <SelectItem value="Presencial">Presenciais</SelectItem>
            </SelectContent>
          </Select>

          {periodFilter !== "customizado" ? (
            <Select onValueChange={onPeriodFilterChange} value={periodFilter}>
              <SelectTrigger
                className={`w-44 border-border bg-surface ${periodFilter !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
              >
                <SelectValue placeholder="Qualquer data" />
              </SelectTrigger>
              <SelectContent className="border-border bg-surface">
                <SelectItem value="todos">Qualquer data</SelectItem>
                <SelectItem value="este-mes">Este mês</SelectItem>
                <SelectItem value="proximo-mes">Próximo mês</SelectItem>
                <SelectItem value="este-ano">Este ano</SelectItem>
                <SelectItem value="customizado">Customizado</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-orange-500 bg-orange-500/10 px-3 py-2">
              <InputMask mask="99/99/9999" value={startText} onChange={(e) => setStartText(e.target.value)} onBlur={handleStartBlur}>
                {(inputProps: any) => (
                  <input {...inputProps} placeholder="dd/mm/yyyy" className="w-24 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none" />
                )}
              </InputMask>
              <i className="fi fi-ts-arrow-right flex items-center text-sm text-orange-500/60" />
              <InputMask mask="99/99/9999" value={endText} onChange={(e) => setEndText(e.target.value)} onBlur={handleEndBlur}>
                {(inputProps: any) => (
                  <input {...inputProps} placeholder="dd/mm/yyyy" className="w-24 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none" />
                )}
              </InputMask>
              <button onClick={handleRemovePeriod} className="ml-1 flex items-center text-orange-500/60 transition-colors hover:text-orange-500">
                <i className="fi fi-rs-x flex items-center text-[10px]" />
              </button>
            </div>
          )}

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground">Status</span>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((option) => {
                  const selected = draftInscription.includes(option.value)
                  return (
                    <button
                      key={option.value}
                      onClick={() => {
                        const next = selected
                          ? draftInscription.filter((v) => v !== option.value)
                          : [...draftInscription, option.value]
                        setDraftInscription(next)
                      }}
                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                        selected
                          ? "border-orange-500 bg-orange-500/10 text-orange-500"
                          : "border-border bg-black/40 text-muted-foreground"
                      }`}
                    >
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Tipo</span>
              <Select onValueChange={setDraftType} value={draftType}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftType !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todos os Tipos" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Todos os Tipos</SelectItem>
                  <SelectItem value="Onlive">Onlives</SelectItem>
                  <SelectItem value="Presencial">Presenciais</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Período</span>
              <Select onValueChange={setDraftPeriod} value={draftPeriod}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftPeriod !== "todos" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Qualquer data" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="todos">Qualquer data</SelectItem>
                  <SelectItem value="este-mes">Este mês</SelectItem>
                  <SelectItem value="proximo-mes">Próximo mês</SelectItem>
                  <SelectItem value="este-ano">Este ano</SelectItem>
                  <SelectItem value="customizado">Customizado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {draftPeriod === "customizado" && (
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">Intervalo</span>
                <div className="flex items-center gap-2 rounded-lg border border-orange-500 bg-orange-500/10 px-4 py-2">
                  <InputMask mask="99/99/9999" value={draftStartText} onChange={(e) => setDraftStartText(e.target.value)}>
                    {(inputProps: any) => (
                      <input {...inputProps} placeholder="dd/mm/yyyy" className="flex-1 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none" />
                    )}
                  </InputMask>
                  <i className="fi fi-ts-arrow-right flex items-center text-sm text-orange-500/60" />
                  <InputMask mask="99/99/9999" value={draftEndText} onChange={(e) => setDraftEndText(e.target.value)}>
                    {(inputProps: any) => (
                      <input {...inputProps} placeholder="dd/mm/yyyy" className="flex-1 bg-transparent text-sm text-orange-400 placeholder-orange-500/40 focus:outline-none" />
                    )}
                  </InputMask>
                </div>
              </div>
            )}
          </div>

          <DrawerFooter>
            <Button onClick={applyFilters} className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600">
              Aplicar filtro
            </Button>
            <Button onClick={clearFilters} variant="ghost" className="w-full text-muted-foreground hover:text-white">
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
